import { useMemo, useState } from "react";
import {
  formatLong,
  isBusy,
  monthLabel,
  rangeHitsBusy,
  todayIso,
} from "../../lib/dates";
import type { DateRange } from "../../lib/bookingApi";

interface Props {
  ranges: DateRange[];
  startDate: string | null;
  endDate: string | null;
  onChange: (startDate: string | null, endDate: string | null) => void;
  loading?: boolean;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const AvailabilityCalendar = ({ ranges, startDate, endDate, onChange, loading }: Props) => {
  const today = todayIso();
  const initial = startDate ? new Date(`${startDate}T00:00:00`) : new Date();
  const [cursor, setCursor] = useState({ year: initial.getFullYear(), month: initial.getMonth() });

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const items: { iso: string | null; day: number | null }[] = [];
    for (let i = 0; i < startPad; i += 1) {
      items.push({ iso: null, day: null });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      items.push({ iso, day });
    }
    while (items.length % 7 !== 0) {
      items.push({ iso: null, day: null });
    }
    return items;
  }, [cursor]);

  const pickup = startDate;
  const returnDate = endDate;

  function select(iso: string) {
    if (iso < today) {
      return;
    }
    if (!pickup || returnDate) {
      if (isBusy(iso, ranges)) {
        return;
      }
      onChange(iso, null);
      return;
    }
    if (iso === pickup) {
      onChange(null, null);
      return;
    }
    if (iso < pickup) {
      if (isBusy(iso, ranges)) {
        return;
      }
      onChange(iso, null);
      return;
    }
    if (rangeHitsBusy(pickup, iso, ranges)) {
      if (!isBusy(iso, ranges)) {
        onChange(iso, null);
      }
      return;
    }
    onChange(pickup, iso);
  }

  function inSelection(iso: string): boolean {
    if (!pickup) {
      return false;
    }
    if (!returnDate) {
      return iso === pickup;
    }
    return iso >= pickup && iso < returnDate;
  }

  function canClick(iso: string): boolean {
    if (iso < today) {
      return false;
    }
    if (!pickup || returnDate) {
      return !isBusy(iso, ranges);
    }
    if (iso <= pickup) {
      return !isBusy(iso, ranges) || iso === pickup;
    }
    return !rangeHitsBusy(pickup, iso, ranges);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          className="px-3 py-1 rounded-xl text-alt-blk hover:bg-zinc-100"
          onClick={() =>
            setCursor((c) =>
              c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 },
            )
          }
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="font-semibold text-lg">{monthLabel(cursor.year, cursor.month)}</div>
        <button
          type="button"
          className="px-3 py-1 rounded-xl text-alt-blk hover:bg-zinc-100"
          onClick={() =>
            setCursor((c) =>
              c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 },
            )
          }
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-zinc-500 mb-2">
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, index) => {
          if (!cell.iso) {
            return <div key={`empty-${index}`} className="h-10" />;
          }
          const iso = cell.iso;
          const reserved = isBusy(iso, ranges);
          const past = iso < today;
          const selected = inSelection(iso);
          const isStart = pickup === iso;
          const isCheckout = returnDate === iso;
          const disabled = !canClick(iso);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => select(iso)}
              className={[
                "h-10 rounded-xl text-sm transition-colors",
                disabled ? "text-zinc-300 cursor-not-allowed line-through" : "hover:bg-sunset/20",
                selected && !isCheckout ? "bg-mars text-white hover:bg-sunset" : "",
                isStart ? "bg-mars text-white" : "",
                isCheckout && pickup ? "ring-2 ring-mars text-mars" : "",
                iso === today && !selected ? "ring-1 ring-turquoise" : "",
              ].join(" ")}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mt-4">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-mars inline-block" /> Nights
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded ring-2 ring-mars inline-block" /> Return
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-zinc-200 inline-block" /> Reserved
        </span>
      </div>

      <p className="text-sm text-zinc-600 mt-4">
        {loading
          ? "Checking availability…"
          : pickup && returnDate
            ? `${formatLong(pickup)} pickup → ${formatLong(returnDate)} return`
            : pickup
              ? `${formatLong(pickup)} pickup — now choose a return date`
              : "Select a pickup date, then a return date."}
      </p>
    </div>
  );
};

export default AvailabilityCalendar;
